import { useMemo } from 'react';
import { queryOptions, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { groupCatalog, operationForSku, type RawCatalogEntry } from '../../lib/catalogMap';
import type { PublishedPrices } from '../../lib/contentPrice';
import { CATALOG_FRESH_TIME, fetchCatalogEntries, fetchPublishedPrices } from './repository';
import { catalogDetail } from './editorial';
import type { ApiDetail } from './model';
import { isPrerenderRequest, prerenderInput } from '../prerender/buildInput';
import { loadCatalogText } from '../../i18n/catalogText';
import { localizedDetail } from '../../i18n/catalogData';
import { platformKey, platformKeyForRoute } from '../../lib/catalogPlatform';

export const catalogQueryKeys = {
  all: ['catalog'] as const,
};

export const catalogQueryOptions = () =>
  queryOptions<RawCatalogEntry[]>({
    queryKey: catalogQueryKeys.all,
    queryFn: fetchCatalogEntries,
    staleTime: CATALOG_FRESH_TIME,
    refetchInterval: CATALOG_FRESH_TIME,
  });

/** Raw, locale-neutral data for a complete platform, including the prices in
 * related endpoint links. Never store this subset under the full catalog key. */
export const platformCatalogQueryOptions = (queryClient: QueryClient, apiId: string) => {
  const selectPlatform = (entries: RawCatalogEntry[]) =>
    entries.filter((entry) => platformKey(entry.slug) === platformKeyForRoute(apiId));
  return queryOptions<RawCatalogEntry[]>({
    queryKey: [...catalogQueryKeys.all, 'platform', apiId] as const,
    queryFn: async () => selectPlatform(await fetchCatalogEntries()),
    initialData: () => {
      const entries = queryClient.getQueryData(catalogQueryOptions().queryKey);
      return entries ? selectPlatform(entries) : undefined;
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(catalogQueryKeys.all)?.dataUpdatedAt,
    staleTime: CATALOG_FRESH_TIME,
    refetchInterval: CATALOG_FRESH_TIME,
  });
};

const detailQueryOptions = (queryClient: QueryClient, apiId: string, scope: 'all' | 'platform') =>
  scope === 'platform' ? platformCatalogQueryOptions(queryClient, apiId) : catalogQueryOptions();

/**
 * Discovery is a hard precondition at prerender and a tolerated enhancement at
 * runtime, and this is the only place that rule lives. The build must fail loudly
 * on an unreachable or unauthoritative catalog rather than emit pages with no
 * catalog in them. A live request must instead degrade, because a rejected loader
 * drops the route to an error boundary and replaces a fully rendered page with a
 * full-page error over one transient fetch.
 */
const tolerateAtRuntime = <T,>(work: Promise<T>, degraded: () => T): Promise<T> =>
  isPrerenderRequest() ? work : work.catch(degraded);

/** Loaders that read the catalog also load its translations (i18n/catalogText.ts),
 *  so the server render has them. */
export const ensureCatalogEntries = async (queryClient: QueryClient) => {
  const [entries] = await Promise.all([
    tolerateAtRuntime(
      queryClient.ensureQueryData(catalogQueryOptions()),
      (): RawCatalogEntry[] => [],
    ),
    loadCatalogText(),
  ]);
  return entries;
};

/**
 * Published pricing for pages that quote AnyAPI's price. Never stale: a post
 * states the price its build read, and refreshing it in the browser would
 * download the whole catalog on every article view to redraw the same figures.
 */
const publishedPricesQueryOptions = () =>
  queryOptions({
    queryKey: [...catalogQueryKeys.all, 'prices'] as const,
    queryFn: fetchPublishedPrices,
    staleTime: Infinity,
  });

/**
 * The content sandbox build is the one prerender without prices: it has no
 * gateway to ask, carries no catalog snapshot, and prerenders only blog posts
 * (`vite.config.ts`, `scripts/prerenderScope.ts`), so its tags render a dash.
 * The shipped build always carries the snapshot.
 */
export const ensurePublishedPrices = (queryClient: QueryClient) => {
  const prices = queryClient.ensureQueryData(publishedPricesQueryOptions());
  const none = (): PublishedPrices => ({});
  return prerenderInput('catalog') === undefined ? prices.catch(none) : tolerateAtRuntime(prices, none);
};

/** Published pricing, or undefined only when a live request could not reach
 *  the catalog; the build fails instead. */
export const usePublishedPrices = (): PublishedPrices | undefined =>
  useQuery(publishedPricesQueryOptions()).data;

const DEFAULT_PLATFORM_ICON = 'message-circle';

/**
 * Route-loader detail resolution. Degrading has to cover the authority check too,
 * not just the fetch: `catalogDetail` throws for a published platform with no
 * live record, so an empty catalog would otherwise re-throw straight past the
 * tolerated fetch. A null detail lets the page fall back to its server-rendered
 * copy, or render its own not-found state when there is genuinely nothing.
 *
 * The platform's mark comes with it, resolved from the catalog list the way the
 * catalog page resolves it: the list carries the icon, the detail does not.
 */
export const loadCatalogDetail = (
  queryClient: QueryClient,
  routeId: string,
  scope: 'all' | 'platform' = 'all',
) =>
  tolerateAtRuntime(
    (async () => {
      const [entries] = await Promise.all([
        queryClient.ensureQueryData(detailQueryOptions(queryClient, routeId, scope)),
        loadCatalogText(),
      ]);
      const icon = groupCatalog(entries).find((c) => c.id === routeId)?.icon ?? DEFAULT_PLATFORM_ICON;
      return { detail: localizedDetail(catalogDetail(entries, routeId)), icon };
    })(),
    () => ({ detail: null, icon: DEFAULT_PLATFORM_ICON }),
  );

/**
 * The one owner of "which platform detail does this page show": the live
 * projection once discovery has answered, the server-rendered copy until then.
 *
 * Memoized deliberately. `catalogDetail` rebuilds the whole object graph on
 * every call, so computing it inline in a component hands every render a new
 * `ApiDetail` identity. That silently re-runs every downstream `useMemo` and
 * `useEffect` keyed on the detail, and an effect that writes state from one of
 * those becomes an unbounded render loop.
 */
export const useCatalogDetail = (
  apiId: string,
  serverRendered: ApiDetail | null,
  scope: 'all' | 'platform' = 'all',
): ApiDetail | null => {
  const queryClient = useQueryClient();
  const entries = useQuery(detailQueryOptions(queryClient, apiId, scope)).data;
  return useMemo(
    () => (entries ? localizedDetail(catalogDetail(entries, apiId)) : serverRendered),
    [entries, apiId, serverRendered],
  );
};

/** Resolves a SKU's gateway-authored operation, or null while discovery is
 * unavailable. Callers render a skeleton or omit the snippet rather than
 * inventing a route. */
export const useCatalogOperation = (sku: string) => useCatalogOperationState(sku).operation;

/** Resolves an operation for a local boundary that must preserve surrounding
 * user data when discovery is unavailable. A resolved operation never reports an
 * error: a failed background refresh leaves the last good snapshot in place. */
export const useCatalogOperationState = (sku: string) => {
  const catalog = useQuery(catalogQueryOptions());
  if (!catalog.data) return { error: catalog.error, operation: null };
  try {
    return { error: null, operation: operationForSku(catalog.data, sku) };
  } catch (failure) {
    return {
      error: failure instanceof Error ? failure : new Error(String(failure)),
      operation: null,
    };
  }
};
